import { computed, ref, watch } from 'vue';
import { defineStore } from 'pinia';
import { APP_CONFIG } from '@/config/app';
import { cmsAPI } from '@/services/api';
import { getOperatorCmsConfig } from '@/config/cms-containers';
import { currentLocale, i18n, t } from '@/i18n';

const CACHE_TTL_MS = 5 * 60 * 1000;
const requestCache = new Map();
const requestInflight = new Map();

const asArray = (value) => (Array.isArray(value) ? value : []);
const asString = (value, fallback = '') => {
  const normalized = String(value || '').trim();
  return normalized || fallback;
};
const isAbsoluteUrl = (value) => /^https?:\/\//i.test(asString(value));

const cloneData = (value) => {
  if (value === null || value === undefined) return value;

  try {
    if (typeof structuredClone === 'function') {
      return structuredClone(value);
    }
  } catch (err) {
    // fallthrough
  }

  try {
    return JSON.parse(JSON.stringify(value));
  } catch (err) {
    return value;
  }
};

const buildRequestCacheKey = ({
  type,
  operator,
  locale = 'es',
  code = '',
  page = 1,
  limit = 0,
}) => [
  String(type || '').trim().toUpperCase(),
  String(operator || '').trim().toUpperCase(),
  String(locale || 'es').trim().toLowerCase(),
  String(code || '').trim(),
  String(page || 1),
  String(limit || 0),
].join('|');

const readRequestCache = (key) => {
  const entry = requestCache.get(key);
  if (!entry) return null;

  if (entry.expiresAt < Date.now()) {
    requestCache.delete(key);
    return null;
  }

  return cloneData(entry.value);
};

const writeRequestCache = (key, value) => {
  requestCache.set(key, {
    value: cloneData(value),
    expiresAt: Date.now() + CACHE_TTL_MS,
  });
};

const getCachedOrFetch = async ({ key, force = false, fetcher }) => {
  if (!force) {
    const cached = readRequestCache(key);
    if (cached) {
      return cached;
    }
  }

  const inflightKey = `${key}::inflight`;
  if (!force && requestInflight.has(inflightKey)) {
    return requestInflight.get(inflightKey);
  }

  const promise = (async () => {
    const response = await fetcher();
    if (response?.success) {
      writeRequestCache(key, response);
      return cloneData(response);
    }
    return response;
  })();

  requestInflight.set(inflightKey, promise);
  try {
    return await promise;
  } finally {
    requestInflight.delete(inflightKey);
  }
};

const toCategoryType = (game) => {
  const rawType = asString(game?.type).toLowerCase();
  const source = `${asString(game?.name)} ${asString(game?.launcherId)}`.toLowerCase();

  if (rawType === 'slot') return 'slot';
  if (rawType === 'table') return 'table';
  if (rawType.includes('live') || game?.liveCasino) return 'live_game';
  if (rawType === 'roulette') return 'roulette';
  if (rawType === 'blackjack') return 'blackjack';
  if (rawType === 'baccarat') return 'baccarat';
  if (rawType === 'poker') return 'poker';

  if (source.includes('live')) return 'live_game';
  if (source.includes('roulette')) return 'roulette';
  if (source.includes('blackjack')) return 'blackjack';
  if (source.includes('baccarat')) return 'baccarat';
  if (source.includes('poker')) return 'poker';

  return 'slot';
};

const mapCmsImageToThumbnail = (images) => {
  const list = asArray(images);
  if (list.length === 0) return APP_CONFIG.gamePlaceholderImage;

  const preferredSizes = new Set(['400x300', '400x400', '600x400', '500x300', '100x100']);
  const preferred = list.find((item) => preferredSizes.has(asString(item?.size)));
  const selected = preferred || list[0];
  const candidate = asString(selected?.url);

  if (!candidate) return APP_CONFIG.gamePlaceholderImage;
  if (isAbsoluteUrl(candidate)) return candidate;
  if (candidate.startsWith('/')) return `${APP_CONFIG.apiBaseUrl}${candidate}`;

  return APP_CONFIG.gamePlaceholderImage;
};

const normalizeLobbyGame = (game, index, lobbyCode) => {
  const launcherId = asString(game?.launcherId, `${lobbyCode}-${index}`);
  const providerName = asString(game?.provider?.name, 'Proveedor');
  const providerCode = asString(game?.provider?.code);

  return {
    id: launcherId,
    code: launcherId,
    gameid: launcherId,
    name: asString(game?.name, launcherId),
    provider: providerName,
    providerName,
    providerCode,
    providerId: '',
    fun: Boolean(game?.demo),
    type: toCategoryType(game),
    featured: index < 12,
    created_at: null,
    thumbnail: mapCmsImageToThumbnail(game?.images),
    lobbyCode,
    source: 'cms',
  };
};

const normalizeAnnouncementContainer = (containerKey, announcements) => ({
  id: containerKey,
  key: containerKey,
  type: 'announcement',
  title: 'Anuncios',
  items: asArray(announcements).map((item) => ({
    id: asString(item?._id),
    title: asString(item?.title),
    description: asString(item?.description),
    image: asString(item?.image),
    startDate: item?.startDate || null,
    endDate: item?.endDate || null,
    createdAt: item?.createdAt || null,
  })),
});

const normalizeCarouselContainer = (containerKey, carousel) => ({
  id: containerKey,
  key: containerKey,
  type: 'carousel',
  code: asString(carousel?.code),
  title: asString(carousel?.name, t('cms.promotions')),
  items: asArray(carousel?.banners)
    .map((banner, idx) => ({
      id: asString(banner?.id, `${containerKey}-${idx}`),
      title: asString(banner?.title, t('cms.promotion')),
      subtitle: asString(banner?.subtitle),
      ctaText: asString(banner?.ctaText, t('cms.seeMore')),
      imageDesktop: asString(banner?.imageDesktop),
      imageMobile: asString(banner?.imageMobile),
      link: asString(banner?.link),
      target: asString(banner?.target, '_self'),
      requiresAuth: Boolean(banner?.requiresAuth),
      order: Number(banner?.order || 0),
    }))
    .sort((a, b) => a.order - b.order),
});

const normalizeLobbyContainer = (containerKey, lobby) => {
  const lobbyCode = asString(lobby?.code);
  const title = asString(lobby?.texts?.title, 'Lobby');
  const subtitle = asString(lobby?.texts?.subtitle);
  const description = asString(lobby?.texts?.description);
  const games = asArray(lobby?.games).map((game, index) => normalizeLobbyGame(game, index, lobbyCode));

  return {
    id: containerKey,
    key: containerKey,
    type: 'lobby',
    code: lobbyCode,
    lobbyType: asString(lobby?.lobbyType),
    gameType: asString(lobby?.gameType),
    liveCasino: Boolean(lobby?.liveCasino),
    title,
    subtitle,
    description,
    visualConfig: lobby?.visualConfig || {},
    providers: asArray(lobby?.providers),
    pagination: lobby?.pagination || null,
    games,
  };
};

const uniqueGames = (games) => {
  const dedup = new Map();
  for (const game of asArray(games)) {
    if (!game?.id) continue;
    if (!dedup.has(game.id)) dedup.set(game.id, game);
  }
  return Array.from(dedup.values());
};

const normalizeLobbyQuery = (payload = {}, fallbackLimit = 24) => {
  const page = Number(payload?.page || 1);
  const limit = Number(payload?.limit || fallbackLimit);
  const total = Number(payload?.total || 0);
  const totalPages = Number(payload?.totalPages || 1);

  return {
    page: Number.isFinite(page) && page > 0 ? page : 1,
    limit: Number.isFinite(limit) && limit > 0 ? limit : fallbackLimit,
    total: Number.isFinite(total) && total >= 0 ? total : 0,
    totalPages: Number.isFinite(totalPages) && totalPages > 0 ? totalPages : 1,
    loading: false,
    error: '',
  };
};

const getContainerKeyByLobbyCode = (containers, lobbyCode) => {
  const found = asArray(containers).find((item) => item?.type === 'lobby' && item?.code === lobbyCode);
  return found?.key || '';
};

const splitDefinitionsByPriority = (containerDefs, viewName) => {
  const critical = [];
  const deferred = [];

  const isCriticalByFallback = (definition, index) => {
    if (viewName === 'home') {
      return index < 3;
    }

    if (viewName === 'games') {
      if (definition.type !== 'lobby') return false;
      const lobbiesBefore = containerDefs
        .slice(0, index + 1)
        .filter((item) => item.type === 'lobby')
        .length;
      return lobbiesBefore <= 2;
    }

    if (viewName === 'lobby') {
      return true;
    }

    return true;
  };

  containerDefs.forEach((definition, index) => {
    const explicitPriority = asString(definition?.priority).toLowerCase();
    const shouldBeCritical = explicitPriority
      ? explicitPriority === 'critical'
      : isCriticalByFallback(definition, index);

    if (shouldBeCritical) {
      critical.push(definition);
      return;
    }

    deferred.push(definition);
  });

  if (!critical.length && containerDefs.length) {
    critical.push(containerDefs[0]);
    return {
      critical,
      deferred: containerDefs.slice(1),
    };
  }

  return { critical, deferred };
};

const toOrderedContainers = (containerDefs, containerMap) => {
  return containerDefs
    .map((definition) => containerMap.get(definition.key))
    .filter(Boolean);
};

export const useCmsContentStore = defineStore('cmsContent', () => {
  const containers = ref([]);
  const games = ref([]);
  const loading = ref(false);
  const loaded = ref(false);
  const initialized = ref(false);
  const error = ref('');
  const operator = ref(APP_CONFIG.operator);
  const locale = ref('es');
  const view = ref('home');
  const containerStatus = ref({});
  const lobbyQueries = ref({});

  let inflightRequest = null;

  const lobbyContainers = computed(() => containers.value.filter((container) => container.type === 'lobby'));
  const carouselContainers = computed(() => containers.value.filter((container) => container.type === 'carousel'));
  const announcementContainers = computed(() => containers.value.filter((container) => container.type === 'announcement'));

  const slotGames = computed(() => games.value.filter((game) => game.type === 'slot'));
  const tableGames = computed(() => games.value.filter((game) => ['table', 'roulette', 'blackjack', 'baccarat', 'poker'].includes(game.type)));
  const liveGames = computed(() => games.value.filter((game) => game.type.startsWith('live')));
  const featuredGames = computed(() => games.value.filter((game) => game.featured));

  const heroContainer = computed(() => {
    const list = carouselContainers.value;
    if (!list.length) return null;

    const preferred = list.find((item) => asString(item?.key).includes('main'));
    return preferred || list[0];
  });

  const promoCarouselContainers = computed(() => {
    const heroKey = heroContainer.value?.key;
    return carouselContainers.value.filter((item) => item.key !== heroKey);
  });

  const announcementItems = computed(() => announcementContainers.value.flatMap((container) => asArray(container.items)));

  const rebuildGames = () => {
    const merged = containers.value
      .filter((container) => container.type === 'lobby')
      .flatMap((container) => asArray(container.games));

    games.value = uniqueGames(merged);
  };

  const setContainerStatus = (key, status) => {
    containerStatus.value = {
      ...containerStatus.value,
      [key]: {
        ...(containerStatus.value[key] || {}),
        ...status,
      },
    };
  };

  const setLobbyQuery = (lobbyCode, query) => {
    lobbyQueries.value = {
      ...lobbyQueries.value,
      [lobbyCode]: {
        ...(lobbyQueries.value[lobbyCode] || {}),
        ...query,
      },
    };
  };

  const getLobbyContainerByCode = (lobbyCode) => lobbyContainers.value.find((item) => item.code === lobbyCode) || null;

  const resetState = () => {
    containers.value = [];
    games.value = [];
    error.value = '';
    containerStatus.value = {};
    lobbyQueries.value = {};
    loaded.value = false;
  };

  const fetchAnnouncements = async ({ resolvedOperator, force }) => {
    const key = buildRequestCacheKey({
      type: 'announcement',
      operator: resolvedOperator,
      page: 1,
      limit: 50,
    });

    return getCachedOrFetch({
      key,
      force,
      fetcher: () => cmsAPI.getAnnouncements({
        operator: resolvedOperator,
        page: 1,
        limit: 50,
      }),
    });
  };

  const fetchCarousels = async ({ resolvedOperator, resolvedLocale, codes, force }) => {
    const sortedCodes = [...codes].sort();
    const key = buildRequestCacheKey({
      type: 'carousel',
      operator: resolvedOperator,
      locale: resolvedLocale,
      code: sortedCodes.join(','),
      page: 0,
      limit: 50,
    });

    return getCachedOrFetch({
      key,
      force,
      fetcher: () => cmsAPI.getCarousels({
        operator: resolvedOperator,
        carouselCodes: sortedCodes,
        locale: resolvedLocale,
        offset: 0,
        limit: 50,
      }),
    });
  };

  const fetchLobby = async ({ definition, resolvedOperator, resolvedLocale, force }) => {
    const page = Number(definition.page || 1);
    const limit = Number(definition.limit || 24);
    const code = asString(definition.lobbyCode);

    const key = buildRequestCacheKey({
      type: 'lobby',
      operator: resolvedOperator,
      locale: resolvedLocale,
      code,
      page,
      limit,
    });

    return getCachedOrFetch({
      key,
      force,
      fetcher: () => cmsAPI.getLobby({
        operator: resolvedOperator,
        lobbyCode: code,
        page,
        limit,
        locale: resolvedLocale,
      }),
    });
  };

  const applyContainer = ({ containerDefs, containerMap }) => {
    containers.value = toOrderedContainers(containerDefs, containerMap);
    rebuildGames();
  };

  const processDefinitionsPhase = async ({
    definitions,
    containerDefs,
    resolvedOperator,
    resolvedLocale,
    force,
    containerMap,
    nextLobbyQueries,
  }) => {
    if (!definitions.length) {
      return;
    }

    const needsAnnouncements = definitions.some((item) => item.type === 'announcement');
    const carouselDefinitions = definitions.filter((item) => item.type === 'carousel');
    const lobbyDefinitions = definitions.filter((item) => item.type === 'lobby');

    const announcementResult = needsAnnouncements
      ? await fetchAnnouncements({ resolvedOperator, force })
      : { success: true, data: [] };

    const carouselCodes = Array.from(new Set(
      carouselDefinitions
        .map((definition) => asString(definition.code))
        .filter(Boolean)
    ));

    const carouselsResult = carouselCodes.length
      ? await fetchCarousels({
        resolvedOperator,
        resolvedLocale,
        codes: carouselCodes,
        force,
      })
      : { success: true, data: [] };

    const carouselByCode = new Map(asArray(carouselsResult?.data).map((item) => [asString(item?.code), item]));

    const lobbyResults = await Promise.all(
      lobbyDefinitions.map(async (definition) => {
        const result = await fetchLobby({
          definition,
          resolvedOperator,
          resolvedLocale,
          force,
        });
        return [definition.key, result];
      })
    );
    const lobbyByKey = new Map(lobbyResults);

    for (const definition of definitions) {
      try {
        if (definition.type === 'announcement') {
          if (!announcementResult.success) {
            throw new Error(announcementResult?.error?.message || t('errors.cms.announcements'));
          }

          const normalized = normalizeAnnouncementContainer(definition.key, announcementResult.data);
          containerMap.set(definition.key, normalized);
          setContainerStatus(definition.key, {
            loading: false,
            error: '',
            empty: normalized.items.length === 0,
          });
          applyContainer({ containerDefs, containerMap });
          continue;
        }

        if (definition.type === 'carousel') {
          if (!carouselsResult.success) {
            throw new Error(carouselsResult?.error?.message || t('errors.cms.carousels'));
          }

          const carousel = carouselByCode.get(asString(definition.code)) || null;
          const normalized = normalizeCarouselContainer(definition.key, carousel);
          containerMap.set(definition.key, normalized);
          setContainerStatus(definition.key, {
            loading: false,
            error: '',
            empty: normalized.items.length === 0,
          });
          applyContainer({ containerDefs, containerMap });
          continue;
        }

        if (definition.type === 'lobby') {
          const lobbyResult = lobbyByKey.get(definition.key);
          if (!lobbyResult?.success) {
            throw new Error(lobbyResult?.error?.message || t('errors.cms.lobbyLoad', { code: definition.lobbyCode }));
          }

          const normalized = normalizeLobbyContainer(definition.key, lobbyResult.data);
          containerMap.set(definition.key, normalized);
          setContainerStatus(definition.key, {
            loading: false,
            error: '',
            empty: normalized.games.length === 0,
          });

          const pagination = normalizeLobbyQuery(lobbyResult?.data?.pagination, Number(definition.limit || 24));
          nextLobbyQueries[normalized.code] = pagination;

          applyContainer({ containerDefs, containerMap });
        }
      } catch (containerError) {
        setContainerStatus(definition.key, {
          loading: false,
          error: containerError?.message || t('errors.cms.container'),
          empty: true,
        });
      }
    }
  };

  const loadContent = async ({
    force = false,
    operator: nextOperator = APP_CONFIG.operator,
    locale: nextLocale,
    view: nextView = 'home',
  } = {}) => {
    const resolvedOperator = asString(nextOperator || APP_CONFIG.operator, APP_CONFIG.operator).toUpperCase();
    const resolvedView = asString(nextView || 'home', 'home').toLowerCase();
    const config = getOperatorCmsConfig(resolvedOperator, resolvedView);
    // El idioma activo del sitio manda; el `locale` del config del operador queda como respaldo.
    const resolvedLocale = asString(nextLocale || currentLocale() || config?.locale || 'es', 'es').toLowerCase();
    // Cambio solo de idioma sobre una vista ya cargada: no se vacía la pantalla (sin skeleton),
    // se mantiene el contenido actual y cada sección se reemplaza cuando llega la nueva versión.
    const isLocaleOnlyChange = !force
      && loaded.value
      && operator.value === resolvedOperator
      && view.value === resolvedView
      && locale.value !== resolvedLocale;
    const shouldReset = !isLocaleOnlyChange && (force
      || !loaded.value
      || operator.value !== resolvedOperator
      || locale.value !== resolvedLocale
      || view.value !== resolvedView);

    if (
      !force
      && loaded.value
      && operator.value === resolvedOperator
      && locale.value === resolvedLocale
      && view.value === resolvedView
    ) {
      return { success: true, data: { containers: containers.value, games: games.value } };
    }

    if (loading.value && inflightRequest) {
      return inflightRequest;
    }

    if (shouldReset) {
      resetState();
    }

    loading.value = true;
    error.value = '';
    operator.value = resolvedOperator;
    locale.value = resolvedLocale;
    view.value = resolvedView;

    const containerDefs = asArray(config?.containers);

    // En un cambio solo de idioma no se marcan las secciones como "cargando": el contenido ya está en
    // pantalla y así no aparecen skeletons extra mientras llega la versión en el nuevo idioma.
    for (const definition of isLocaleOnlyChange ? [] : containerDefs) {
      setContainerStatus(definition.key, {
        loading: true,
        error: '',
        empty: false,
      });
    }

    const { critical, deferred } = splitDefinitionsByPriority(containerDefs, resolvedView);

    const containerMap = new Map(
      isLocaleOnlyChange ? containers.value.map((container) => [container.key, container]) : [],
    );
    const nextLobbyQueries = isLocaleOnlyChange ? { ...lobbyQueries.value } : {};

    inflightRequest = (async () => {
      try {
        await processDefinitionsPhase({
          definitions: critical,
          containerDefs,
          resolvedOperator,
          resolvedLocale,
          force,
          containerMap,
          nextLobbyQueries,
        });

        lobbyQueries.value = {
          ...nextLobbyQueries,
        };

        loaded.value = true;
        initialized.value = true;

        if (!containers.value.length) {
          const firstCriticalError = critical
            .map((definition) => containerStatus.value[definition.key]?.error)
            .find((message) => message);
          if (firstCriticalError) {
            error.value = firstCriticalError;
          }
        }

        if (deferred.length) {
          await processDefinitionsPhase({
            definitions: deferred,
            containerDefs,
            resolvedOperator,
            resolvedLocale,
            force,
            containerMap,
            nextLobbyQueries,
          });

          lobbyQueries.value = {
            ...nextLobbyQueries,
          };
        }

        return {
          success: true,
          data: {
            containers: containers.value,
            games: games.value,
          },
        };
      } catch (requestError) {
        resetState();
        error.value = requestError?.message || t('errors.cms.load');
        loaded.value = false;
        initialized.value = true;
        return { success: false, error: error.value };
      } finally {
        loading.value = false;
        inflightRequest = null;
      }
    })();

    return inflightRequest;
  };

  const loadLobbyPage = async ({ lobbyCode, page = 1, limit, locale: nextLocale } = {}) => {
    const code = asString(lobbyCode);
    if (!code) {
      return { success: false, error: { message: t('errors.cms.invalidLobby') } };
    }

    const lobbyContainer = getLobbyContainerByCode(code);
    if (!lobbyContainer) {
      return { success: false, error: { message: t('errors.cms.lobbyNotLoaded', { code }) } };
    }

    const key = getContainerKeyByLobbyCode(containers.value, code);
    const currentQuery = lobbyQueries.value[code] || normalizeLobbyQuery({}, Number(limit || 24));
    const resolvedLimit = Number(limit || currentQuery.limit || 24);
    const resolvedPage = Number(page || 1);
    const resolvedLocale = asString(nextLocale || locale.value || 'es', 'es').toLowerCase();

    setLobbyQuery(code, { loading: true, error: '' });
    if (key) {
      setContainerStatus(key, { loading: true, error: '' });
    }

    const cacheKey = buildRequestCacheKey({
      type: 'lobby',
      operator: operator.value,
      locale: resolvedLocale,
      code,
      page: resolvedPage,
      limit: resolvedLimit,
    });

    const response = await getCachedOrFetch({
      key: cacheKey,
      force: false,
      fetcher: () => cmsAPI.getLobby({
        operator: operator.value,
        lobbyCode: code,
        page: resolvedPage,
        limit: resolvedLimit,
        locale: resolvedLocale,
      }),
    });

    if (!response.success) {
      const message = response?.error?.message || t('errors.cms.lobbyLoad', { code });
      setLobbyQuery(code, { loading: false, error: message });
      if (key) {
        setContainerStatus(key, { loading: false, error: message });
      }
      return response;
    }

    const normalized = normalizeLobbyContainer(lobbyContainer.key, response.data);
    const nextContainers = [...containers.value];
    const index = nextContainers.findIndex((item) => item.type === 'lobby' && item.code === code);
    if (index >= 0) {
      nextContainers[index] = normalized;
      containers.value = nextContainers;
      rebuildGames();
    }

    const normalizedQuery = normalizeLobbyQuery(response?.data?.pagination, resolvedLimit);
    setLobbyQuery(code, normalizedQuery);

    if (key) {
      setContainerStatus(key, {
        loading: false,
        error: '',
        empty: normalized.games.length === 0,
      });
    }

    return { success: true, data: normalized };
  };

  const prefetchLobbyPage = async ({ lobbyCode, page = 1, limit = 24, locale: nextLocale } = {}) => {
    const code = asString(lobbyCode);
    if (!code) return { success: false, error: { message: t('errors.cms.invalidLobby') } };

    const resolvedLimit = Number(limit || 24);
    const resolvedPage = Number(page || 1);
    const resolvedLocale = asString(nextLocale || locale.value || 'es', 'es').toLowerCase();

    const cacheKey = buildRequestCacheKey({
      type: 'lobby',
      operator: operator.value,
      locale: resolvedLocale,
      code,
      page: resolvedPage,
      limit: resolvedLimit,
    });

    return getCachedOrFetch({
      key: cacheKey,
      force: false,
      fetcher: () => cmsAPI.getLobby({
        operator: operator.value,
        lobbyCode: code,
        page: resolvedPage,
        limit: resolvedLimit,
        locale: resolvedLocale,
      }),
    });
  };

  // Al cambiar el idioma desde el selector se vuelve a pedir la vista actual en el nuevo idioma
  // (si una carga sigue en curso, se espera a que termine para no descartar el cambio).
  watch(() => i18n.global.locale.value, async () => {
    if (!initialized.value) return;
    if (inflightRequest) await inflightRequest;
    await loadContent({ operator: operator.value, view: view.value });
  });

  return {
    containers,
    games,
    loading,
    loaded,
    initialized,
    error,
    operator,
    locale,
    view,
    containerStatus,
    lobbyQueries,
    lobbyContainers,
    carouselContainers,
    announcementContainers,
    heroContainer,
    promoCarouselContainers,
    announcementItems,
    slotGames,
    tableGames,
    liveGames,
    featuredGames,
    loadContent,
    loadLobbyPage,
    prefetchLobbyPage,
    getLobbyContainerByCode,
  };
});
